import { buildCiIntroCallSummary, PacIntroCallAnswers } from '@bariendo/lib/patient-access-note';
import { buildCiIntroCallScript, PacWidget } from '@bariendo/lib/patient-access-script';
import { physicianFor } from '@bariendo/lib/patient-access-physician';
import { PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID } from '@bariendo/lib/constants';
import { getReferenceString } from '@medplum/core';
import { Communication, Observation, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PatientAccessCallCard } from './PatientAccessCallCard';
import { PacIntroCallSlotGrid } from './PacIntroCallSlotGrid';
import { CherryStatusRow, DoctorBioLines, PhysicianFlag, WeightLossEstimateRow } from './computedWidgets';
import { useCherryStatus } from '@/hooks/useCherryStatus';
import { TimelineContext } from '@/contexts/timelineContexts';
import { summarizeBeat } from './beatSummary';
import { useEligibilityPrefill } from './useEligibilityPrefill';
import { usePatientAccessActions } from './usePatientAccessActions';
import { usePatientAccessCallDraft } from './usePatientAccessCallDraft';
import { ConsultSlot, consultSlotKey, useNpConsultSlots } from './useNpConsultSlots';
import { calculateWeightLossEstimate } from './weightLossEstimate';

interface ScheduledIntroCallCardProps {
  note: Communication;
  answers: PacIntroCallAnswers;
  patient?: Patient;
  /**
   * A note already saved to the chart. Expanding it shows the worked script as
   * a record of the call; it is not an editor. Per the mockup, expanding a
   * saved card "restores the full script" — it does not reopen it for editing.
   */
  readOnly?: boolean;
  onSaved?: () => void;
}

/**
 * The **scheduled** intro call — the 15-minute qualifying call a booked
 * `ci-intro-call` appointment opened. Owns everything specific to that script —
 * the weight-loss estimate, the physician flag and bio, the Cherry and employer
 * actions, and booking the NP consultation — and renders the shared card spine.
 *
 * Legacy, and reached only by `visitType: 'zoom'`. Intake 1.1 replaced this call
 * with the PN consultation and BAR-2612 gave the inbound call its own card; what
 * is left here renders the notes those calls already wrote. New work on the
 * Patient Access scripts belongs on `PnConsultationCard` or `PacIntroCallCard`.
 */
export function ScheduledIntroCallCard({
  note,
  answers: initialAnswers,
  patient,
  readOnly = false,
  onSaved,
}: ScheduledIntroCallCardProps) {
  const medplum = useMedplum();
  const { answers, updateAnswers, setValue, saveToChart, isSaving, saveError } =
    usePatientAccessCallDraft<PacIntroCallAnswers>({
      note,
      initialAnswers,
      answersPayloadId: PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID,
      buildSummary: buildCiIntroCallSummary,
      onSaved,
    });

  /** Memoised so the beat array keeps a stable identity across renders. */
  const script = useMemo(() => buildCiIntroCallScript(), []);

  const [weight, setWeight] = useState<Observation | undefined>();
  /** Availability is only queried while the booking beat is on screen. */
  const [openBeat, setOpenBeat] = useState<string | null>(null);

  const slots = useNpConsultSlots(patient, openBeat === 'book');
  const { handleAction, pendingAction } = usePatientAccessActions(patient);

  // Gated on the pricing beat for the same reason availability is gated on the
  // booking beat: this one reaches Cherry, which rate-limits the lookup, so it
  // must not run for every patient whose timeline is merely opened (BAR-2604).
  //
  // Read through `useContext` rather than `useTimelineContext` deliberately: the
  // latter throws without a provider, and a supplementary status row must not be
  // what makes this card unrenderable outside the timeline. No provider means no
  // check and no row, which is exactly how the card behaved before.
  const onCherryStatus = useContext(TimelineContext)?.onCherryStatus;
  const { status: cherryStatus, loading: cherryChecking } = useCherryStatus(openBeat === 'price', onCherryStatus);

  // Latest body weight backs the weight-loss estimate. A PAC rep has Observation
  // read only; a failure here leaves the estimate blank rather than breaking the card.
  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;

    medplum
      .searchResources('Observation', {
        subject: getReferenceString(patient),
        code: 'http://loinc.org|29463-7',
        _sort: '-date',
        _count: 1,
      })
      .then((results) => {
        if (!cancelled) setWeight(results[0]);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [medplum, patient]);

  // A saved note is read-only: no prefill, since the prefill autosaves into the note.
  useEligibilityPrefill(readOnly ? undefined : patient, updateAnswers);

  const weightLossEstimate = useMemo(() => {
    const procedure = typeof answers.values.procedure === 'string' ? answers.values.procedure : undefined;
    return calculateWeightLossEstimate(weight, procedure);
  }, [answers.values.procedure, weight]);

  const physician = useMemo(() => {
    const location = typeof answers.values.loc === 'string' ? answers.values.loc : undefined;
    return physicianFor(location);
  }, [answers.values.loc]);

  const handleSelectSlot = useCallback(
    async (slot: ConsultSlot) => {
      const previous = answers.slot;

      // Re-tapping the held slot clears it so the rep can pick another time.
      // Compared on the full identity: with several providers free at the same
      // time, matching on `start` made a tap on a different provider look like a
      // re-tap, so the card released the hold instead of switching to it.
      if (previous && consultSlotKey(previous) === consultSlotKey(slot)) {
        updateAnswers((current) => ({ ...current, slot: undefined }));
        await slots.release(previous);
        return;
      }

      const held = await slots.hold(slot);
      if (!held) return;

      updateAnswers((current) => ({ ...current, slot: held }));
      // Hold the new time before giving back the old one, so the rep never
      // drops their place in favour of a slot that turns out to be taken.
      await slots.release(previous);
    },
    [answers.slot, slots, updateAnswers]
  );

  /**
   * Book the held consultation on save, so the rep confirms it as part of
   * closing the call rather than as a separate step.
   *
   * A booking that fails — an expired hold, someone else taking the time — must
   * not trap the rep on an unsaveable card. The failed slot is dropped so the
   * note cannot claim a consultation that does not exist, the error is surfaced
   * by `book`, and saving again charts the call without a booking.
   */
  const handleSave = useCallback(async () => {
    if (answers.slot?.slotId) {
      const failedSlot = answers.slot;
      const booked = await slots.book(failedSlot);
      if (!booked) {
        updateAnswers((current) => ({ ...current, slot: undefined }));
        // The hold may still be standing if the booking failed for any reason
        // other than expiry — give it back rather than sitting on it.
        await slots.release(failedSlot);
        return;
      }
    }
    await saveToChart();
  }, [answers.slot, slots, saveToChart, updateAnswers]);

  /** The widget kinds this script has that the shared spine does not know. */
  const renderCardWidget = useCallback(
    (widget: PacWidget) => {
      if (widget.kind === 'slotGrid') {
        return (
          <PacIntroCallSlotGrid
            slots={slots.slots}
            loading={slots.loading}
            dayOffset={slots.dayOffset}
            maxDayOffset={slots.maxDayOffset}
            onDayChange={slots.setDayOffset}
            selected={answers.slot}
            onSelect={handleSelectSlot}
            disabled={readOnly}
          />
        );
      }

      if (widget.kind !== 'computed') return null;

      switch (widget.field) {
        case 'weightLossEstimate':
          return <WeightLossEstimateRow label={widget.label} say={widget.say} estimate={weightLossEstimate} />;
        case 'doctorBio':
          return <DoctorBioLines physician={physician} />;
        case 'physicianFlag':
          return <PhysicianFlag physician={physician} />;
        case 'cherryStatus':
          return <CherryStatusRow label={widget.label} status={cherryStatus} loading={cherryChecking} />;
        default:
          return null;
      }
    },
    [slots, answers.slot, handleSelectSlot, readOnly, weightLossEstimate, physician, cherryStatus, cherryChecking]
  );

  return (
    <PatientAccessCallCard<PacIntroCallAnswers>
      script={script}
      answers={answers}
      setValue={setValue}
      summarizeBeat={summarizeBeat}
      onAction={handleAction}
      pendingAction={pendingAction}
      renderCardWidget={renderCardWidget}
      readOnly={readOnly}
      isSaving={isSaving || slots.isBooking}
      saveError={saveError}
      onSave={handleSave}
      onOpenBeatChange={setOpenBeat}
    />
  );
}
